// Shared logic for the RADIUS Servers page (authnull-service internal/radius: ListServers,
// CreateServer, RenameServer, DeleteServer). Pure functions, so they are tested without a DOM.
//
// Each FreeRADIUS server's bridge sends a heartbeat every 60 seconds. The API derives the
// status from the last one: healthy (all checks pass), degraded (a check fails), down (no
// heartbeat for 3 minutes), never_connected. The console shows two states: Active and Inactive.

import { RADIUS_CHECK_LABELS, serverIsActive } from "../../../service/radiusService";

export const STATUS_LEGEND =
  "Active: a heartbeat arrived in the last 3 minutes. Inactive: no heartbeat for 3 minutes, or never connected.";

// The server's API status → { active, label, tone } for StatusPill.
export function serverStatusView(server) {
  const active = serverIsActive(server);
  return { active, label: active ? "Active" : "Inactive", tone: active ? "success" : "warn" };
}

// lastSeenAt is an RFC 3339 timestamp (or null); returns ms or null.
export function lastSeenMs(server) {
  const t = Date.parse(server?.lastSeenAt || "");
  return Number.isFinite(t) ? t : null;
}

export function neverConnected(server) {
  return server?.status === "never_connected" || lastSeenMs(server) === null;
}

// "32 s ago", "7 min ago", "3 h ago", "2 d ago". A clock a little ahead of the server's reads "just now".
export function relativeAgo(ms, now = Date.now()) {
  if (!Number.isFinite(ms)) return "";
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s} s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

export function lastHeartbeatText(server, now = Date.now()) {
  return neverConnected(server) ? "Never connected" : relativeAgo(lastSeenMs(server), now);
}

// One line under an Inactive server's status saying what to check; null when Active.
export function inactiveHint(server, now = Date.now()) {
  if (serverIsActive(server)) return null;
  if (neverConnected(server)) return "Waiting for the first heartbeat. Install app.env on the server.";
  return `No heartbeat since ${relativeAgo(lastSeenMs(server), now)}. Check the VM and the heartbeat timer.`;
}

// lastHealth is the last heartbeat body the API stored: { hostname, checks: [{ name, ok, detail }] }.
// It arrives as an object; a JSON string is accepted too.
export function parseHealth(server) {
  let h = server?.lastHealth;
  if (typeof h === "string") {
    try {
      h = JSON.parse(h);
    } catch {
      h = null;
    }
  }
  return h && typeof h === "object" ? h : {};
}

// The five known checks first, in the order the bridge runs them, then anything newer.
const CHECK_ORDER = Object.keys(RADIUS_CHECK_LABELS);

export function healthChecks(server) {
  const checks = Array.isArray(parseHealth(server).checks) ? parseHealth(server).checks : [];
  const rank = (n) => (CHECK_ORDER.includes(n) ? CHECK_ORDER.indexOf(n) : CHECK_ORDER.length);
  return checks
    .filter((c) => c && c.name)
    .map((c) => ({
      name: c.name,
      label: RADIUS_CHECK_LABELS[c.name] || String(c.name).replace(/_/g, " "),
      ok: !!c.ok,
      detail: String(c.detail || ""),
    }))
    .sort((a, b) => rank(a.name) - rank(b.name));
}

// ─── Add / rename validation (messages shown under the field) ────────────────────

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

export const isIPv4 = (v) => IPV4.test(String(v || "").trim());

export const nameTakenMessage = (name) => `A server called ${String(name || "").trim()} already exists.`;

// Names compare case-insensitively, as the API's unique index does.
export function serverNameError(name, servers, exceptId) {
  const n = String(name || "").trim();
  if (!n) return "Enter a name for this server.";
  if (n.length > 128) return "Use at most 128 characters.";
  const taken = (servers || []).some((s) => s.id !== exceptId && String(s.name || "").trim().toLowerCase() === n.toLowerCase());
  return taken ? nameTakenMessage(n) : "";
}

export function sourceIpError(ip) {
  return isIPv4(ip) ? "" : "Enter the server's IPv4 address, for example 10.10.0.20.";
}

// The note under a disabled Delete.
export function deleteBlockedNote(server) {
  const n = Number(server?.devicesUsing) || 0;
  if (n <= 0) return "";
  return `Move its ${n} ${n === 1 ? "device" : "devices"} to another server first.`;
}

// The API's 409 message reads "network devices still send logins to this server": shown as a sentence.
export function sentence(text) {
  const s = String(text || "").trim();
  if (!s) return "";
  const t = s.charAt(0).toUpperCase() + s.slice(1);
  return /[.!?]$/.test(t) ? t : `${t}.`;
}
